import { useState } from 'react';
import { Check, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { VariantStockControl } from '@/components/variant-stock-control';
import { form_error_message } from '@/lib/forms';
import { derive_discount_pct, type CreateVariantInput, type ProductVariant, type UpdateVariantInput } from '@/features/product-variant/types';

type Callbacks = {
  onAdd: (input: CreateVariantInput) => Promise<unknown>;
  onUpdate: (id: string, input: UpdateVariantInput) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  /**
   * Set the count for one size, or null to stop tracking.
   *
   * Optional so surfaces that only price variants are unaffected. Returns the save
   * result so the row can tell the vendor how many waiting shoppers it just notified.
   */
  onSetStock?: (id: string, stock_qty: number | null) => Promise<{ watchers_notified?: number } | void>;
};

/** Parse the three fields into a CreateVariantInput, or return an error message. */
function parse_fields(label: string, price: string, mrp: string): { input: CreateVariantInput } | { error: string } {
  if (!label.trim()) return { error: 'Label is required.' };
  const price_inr = Number(price);
  if (!Number.isFinite(price_inr) || price_inr <= 0) return { error: 'Enter a valid price.' };
  const mrp_inr = mrp.trim() === '' ? null : Number(mrp);
  if (mrp_inr != null && (!Number.isFinite(mrp_inr) || mrp_inr < price_inr)) return { error: 'MRP must be ≥ price.' };
  return { input: { label: label.trim(), price_inr, mrp_inr } };
}

function VariantRow({
  variant,
  onUpdate,
  onDelete,
  onSetStock,
  canDelete,
}: { variant: ProductVariant; canDelete: boolean } & Pick<Callbacks, 'onUpdate' | 'onDelete' | 'onSetStock'>) {
  const [editing, set_editing] = useState(false);
  const [label, set_label] = useState(variant.label);
  const [price, set_price] = useState(variant.price_inr);
  const [mrp, set_mrp] = useState(variant.mrp_inr ?? '');
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  async function save() {
    const parsed = parse_fields(label, price, mrp);
    if ('error' in parsed) return set_error(parsed.error);
    set_busy(true);
    set_error(null);
    try {
      await onUpdate(variant.id, parsed.input);
      set_editing(false);
    } catch (err) {
      set_error(form_error_message(err, 'Could not save.'));
    } finally {
      set_busy(false);
    }
  }
  async function remove() {
    if (!window.confirm(`Delete variant “${variant.label}”?`)) return;
    set_busy(true);
    set_error(null);
    try {
      await onDelete(variant.id);
    } catch (err) {
      set_error(form_error_message(err, 'Could not delete.'));
      set_busy(false);
    }
  }

  if (editing) {
    return (
      <li className="flex flex-col gap-2 rounded-input border-2 border-input bg-secondary/30 p-2.5" data-testid={`variant-edit-${variant.id}`}>
        <div className="grid grid-cols-[1fr_6rem_6rem] items-center gap-2 max-sm:grid-cols-1">
          <Input value={label} onChange={(e) => set_label(e.target.value)} placeholder="Label" data-testid={`variant-edit-label-${variant.id}`} />
          <Input type="number" min={0} value={price} onChange={(e) => set_price(e.target.value)} placeholder="Price" data-testid={`variant-edit-price-${variant.id}`} />
          <Input type="number" min={0} value={mrp} onChange={(e) => set_mrp(e.target.value)} placeholder="MRP" data-testid={`variant-edit-mrp-${variant.id}`} />
        </div>
        {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
        <div className="flex gap-2">
          <Button size="sm" variant="gradient" onClick={save} disabled={busy} data-testid={`variant-edit-save-${variant.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { set_editing(false); set_error(null); }}>
            <X className="h-4 w-4" /> Cancel
          </Button>
        </div>
      </li>
    );
  }

  const discount = variant.discount_pct ?? (variant.mrp_inr ? derive_discount_pct(Number(variant.price_inr), Number(variant.mrp_inr)) : null);
  return (
    <li className="flex flex-col gap-1 py-1.5" data-testid={`variant-${variant.id}`}>
      <div className="flex items-center gap-3">
      <span className="flex-1 text-sm font-medium">{variant.label}</span>
      <span className="font-heading text-sm font-bold tabular-nums">₹{variant.price_inr}</span>
      {variant.mrp_inr && <span className="text-xs text-muted-foreground line-through tabular-nums">₹{variant.mrp_inr}</span>}
      {discount != null && <span className="text-xs font-semibold text-success">-{discount}%</span>}
      <Button size="sm" variant="ghost" onClick={() => set_editing(true)} aria-label="Edit variant" data-testid={`variant-edit-btn-${variant.id}`}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button size="sm" variant="ghost" onClick={remove} disabled={busy || !canDelete} aria-label="Delete variant" title={canDelete ? undefined : 'A product needs at least one variant'} data-testid={`variant-delete-btn-${variant.id}`}>
        <Trash2 className={cn('h-3.5 w-3.5', canDelete && 'text-destructive')} />
      </Button>
      </div>
      {onSetStock && (
        <VariantStockControl
          variant_id={variant.id}
          stock={{
            is_in_stock: variant.is_in_stock ?? true,
            stock_qty: variant.stock_qty ?? null,
            is_low_stock: variant.is_low_stock,
            is_stock_tracked: variant.is_stock_tracked,
          }}
          on_save={(stock_qty) => onSetStock(variant.id, stock_qty)}
          className="pl-0.5"
        />
      )}
    </li>
  );
}

/** Inline variant manager for an existing product. Each action hits the API immediately. */
export function ProductVariantsEditor({ variants, onAdd, onUpdate, onDelete, onSetStock }: { variants: ProductVariant[] } & Callbacks) {
  const [adding, set_adding] = useState(false);
  const [label, set_label] = useState('');
  const [price, set_price] = useState('');
  const [mrp, set_mrp] = useState('');
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  async function add() {
    const parsed = parse_fields(label, price, mrp);
    if ('error' in parsed) return set_error(parsed.error);
    set_busy(true);
    set_error(null);
    try {
      await onAdd(parsed.input);
      set_label(''); set_price(''); set_mrp(''); set_adding(false);
    } catch (err) {
      set_error(form_error_message(err, 'Could not add variant.'));
    } finally {
      set_busy(false);
    }
  }

  return (
    <div className="rounded-input border-2 border-brand-outline-variant/15 bg-card/50 p-3" data-testid="product-variants-editor">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Variants</span>
        {!adding && (
          <Button size="sm" variant="ghost" onClick={() => setAddingOpen(set_adding, set_error)} data-testid="variant-add-open">
            <Plus className="h-4 w-4" /> Add
          </Button>
        )}
      </div>
      <ul className="divide-y divide-brand-outline-variant/10">
        {variants.map((v) => (
          <VariantRow
            key={v.id}
            variant={v}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onSetStock={onSetStock}
            canDelete={variants.length > 1}
          />
        ))}
      </ul>
      {adding && (
        <div className="mt-2 flex flex-col gap-2 rounded-input border-2 border-input bg-secondary/30 p-2.5" data-testid="variant-add-form">
          <div className="grid grid-cols-[1fr_6rem_6rem] items-center gap-2 max-sm:grid-cols-1">
            <Input value={label} onChange={(e) => set_label(e.target.value)} placeholder="Label (e.g. 3 kg)" data-testid="variant-add-label" />
            <Input type="number" min={0} value={price} onChange={(e) => set_price(e.target.value)} placeholder="Price" data-testid="variant-add-price" />
            <Input type="number" min={0} value={mrp} onChange={(e) => set_mrp(e.target.value)} placeholder="MRP" data-testid="variant-add-mrp" />
          </div>
          {error && <p className="text-xs text-destructive" role="alert" data-testid="variant-add-error">{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" variant="gradient" onClick={add} disabled={busy} data-testid="variant-add-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Add variant
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { set_adding(false); set_error(null); }}>Cancel</Button>
          </div>
        </div>
      )}
      {error && !adding && <p className="mt-2 text-xs text-destructive" role="alert">{error}</p>}
    </div>
  );
}

function setAddingOpen(set_adding: (b: boolean) => void, set_error: (e: string | null) => void) {
  set_error(null);
  set_adding(true);
}
